import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Users, Clock, Pencil, WarningCircle } from "@phosphor-icons/react";
import ScheduleEditor from "@/components/ScheduleEditor";

const STATUS_CONFIG = {
  active: { color: "#16A34A", bg: "bg-green-50", border: "border-green-200", label: "Active", dot: "bg-green-500" },
  idle: { color: "#F59E0B", bg: "bg-amber-50", border: "border-amber-200", label: "Idle", dot: "bg-amber-400" },
  absent: { color: "#EF4444", bg: "bg-red-50", border: "border-red-200", label: "Absent", dot: "bg-red-500" },
  offline: { color: "#9CA3AF", bg: "bg-gray-50", border: "border-gray-200", label: "Offline", dot: "bg-gray-400" },
  not_configured: { color: "#D97706", bg: "bg-amber-50", border: "border-amber-200", label: "Not Set", dot: "bg-amber-300" },
};

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

export default function TeamStatusWidget({ compact = false }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "team_leader";
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUserId, setEditUserId] = useState(null);
  const [editUserName, setEditUserName] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules/team-status");
      setMembers(data.members || []);
    } catch (err) { console.warn("Team status error:", err?.message); }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const counts = {
    active: members.filter(m => m.status === "active").length,
    idle: members.filter(m => m.status === "idle").length,
    absent: members.filter(m => m.status === "absent").length,
    offline: members.filter(m => m.status === "offline").length,
    not_configured: members.filter(m => m.status === "not_configured").length,
  };

  const openEditor = (m) => {
    setEditUserId(m.id);
    setEditUserName(m.name);
  };

  if (loading) return <div className="h-32 skeleton-shimmer rounded-lg" />;

  // COMPACT view (dashboard widget)
  if (compact) {
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="team-status-compact">
        <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-3">
          <Users size={16} weight="duotone" className="text-[#1E3A8A]" /> Team Status
        </h3>
        <div className="flex gap-3 mb-3">
          {Object.entries(counts).map(([s, c]) => {
            const cfg = STATUS_CONFIG[s];
            return (
              <div key={s} className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                <span className="text-xs text-[#6B7280]">{c} {cfg.label}</span>
              </div>
            );
          })}
        </div>
        <div className="space-y-1.5">
          {members.slice(0, 8).map(m => {
            const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.offline;
            return (
              <div key={m.id} className="flex items-center gap-2 py-1" data-testid={`status-member-${m.id}`}>
                <div className="relative">
                  <div className="w-7 h-7 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-[10px] font-bold">
                    {m.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${cfg.dot}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#0F172A] truncate">{m.name}</p>
                  <p className="text-[10px] text-[#9CA3AF] truncate">{m.teams?.[0] || m.role?.replace(/_/g, " ")}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cfg.bg} ${cfg.border} border`} style={{ color: cfg.color }}>
                  {cfg.label}
                </span>
              </div>
            );
          })}
        </div>
        {/* Editor dialog */}
        {editUserId && <ScheduleEditor userId={editUserId} userName={editUserName} open={!!editUserId} onClose={() => setEditUserId(null)} onSaved={load} />}
      </div>
    );
  }

  // FULL view
  const now = new Date();
  const currentHour = now.getHours();
  const currentMin = now.getMinutes();

  return (
    <div className="space-y-6" data-testid="team-status-full">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <div key={key} className={`${cfg.bg} ${cfg.border} border rounded-lg p-3 text-center`} data-testid={`status-summary-${key}`}>
            <p className="text-2xl font-bold" style={{ color: cfg.color }} data-testid={`status-count-${key}`}>{counts[key] || 0}</p>
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wide font-medium">{cfg.label}</p>
          </div>
        ))}
      </div>

      {/* Working Hours Timeline */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4">
          <Clock size={16} weight="duotone" className="text-[#1E3A8A]" /> Working Hours Timeline — {now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="working-hours-table">
            <thead>
              <tr>
                <th className="text-left py-2 px-2 text-[10px] font-semibold text-[#6B7280] uppercase w-[160px] min-w-[160px]">Worker</th>
                {HOURS.map(h => (
                  <th key={h} className={`text-center py-2 px-1 text-[10px] font-medium min-w-[44px] ${h === currentHour ? "bg-blue-50 text-[#1E3A8A] font-bold" : "text-[#9CA3AF]"}`}>
                    {String(h).padStart(2, "0")}:00
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const dayName = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
                const daySchedule = m.schedule?.days?.[dayName];
                const isEnabled = daySchedule?.enabled;
                const startHour = isEnabled && daySchedule.start ? parseInt(daySchedule.start.split(":")[0]) : null;
                const endHour = isEnabled && daySchedule.end ? parseInt(daySchedule.end.split(":")[0]) : null;
                const lunchStartH = daySchedule?.lunch_start ? parseInt(daySchedule.lunch_start.split(":")[0]) : null;
                const lunchEndH = daySchedule?.lunch_end ? parseInt(daySchedule.lunch_end.split(":")[0]) : null;
                const breakHours = new Set();
                (daySchedule?.breaks || []).forEach(b => {
                  if (b.time) breakHours.add(parseInt(b.time.split(":")[0]));
                });
                const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.offline;

                return (
                  <tr key={m.id} className="border-t border-[#F3F4F6]" data-testid={`timeline-row-${m.id}`}>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                        <span className="font-medium text-[#0F172A] truncate text-xs">{m.name}</span>
                        {!m.configured && <WarningCircle size={12} className="text-amber-400 flex-shrink-0" title="Not configured" />}
                      </div>
                    </td>
                    {HOURS.map(h => {
                      let cellClass = "bg-gray-50 border border-gray-100"; // off/not scheduled
                      let tooltip = "Off";

                      if (!m.configured) {
                        cellClass = "bg-gray-50 border border-dashed border-gray-200";
                        tooltip = "Not configured";
                      } else if (startHour !== null && h >= startHour && h < endHour) {
                        if (lunchStartH !== null && h >= lunchStartH && h < lunchEndH) {
                          cellClass = "bg-amber-100 border border-amber-200"; // lunch
                          tooltip = "Lunch";
                        } else if (breakHours.has(h)) {
                          cellClass = "bg-orange-100 border border-orange-200"; // break
                          tooltip = "Break";
                        } else if (m.status === "active" && h === currentHour) {
                          cellClass = "bg-green-400 border border-green-500"; // active now
                          tooltip = "Active";
                        } else if (m.status === "absent" && h <= currentHour) {
                          cellClass = "bg-red-200 border border-red-300"; // absent
                          tooltip = "Absent";
                        } else {
                          cellClass = "bg-blue-200 border border-blue-300"; // scheduled
                          tooltip = "Scheduled";
                        }
                      }

                      return (
                        <td key={h} className="py-2 px-0.5">
                          <div className={`h-6 rounded-sm ${cellClass} ${h === currentHour ? "ring-2 ring-[#1E3A8A] ring-offset-1" : ""}`} title={tooltip} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-[#F3F4F6]">
          <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-blue-200 border border-blue-300" /><span className="text-[10px] text-[#6B7280]">Scheduled</span></div>
          <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-green-400 border border-green-500" /><span className="text-[10px] text-[#6B7280]">Active Now</span></div>
          <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-red-200 border border-red-300" /><span className="text-[10px] text-[#6B7280]">Absent</span></div>
          <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-amber-100 border border-amber-200" /><span className="text-[10px] text-[#6B7280]">Lunch</span></div>
          <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-orange-100 border border-orange-200" /><span className="text-[10px] text-[#6B7280]">Break</span></div>
          <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-gray-50 border border-dashed border-gray-200" /><span className="text-[10px] text-[#6B7280]">Not Configured</span></div>
          <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-gray-50 border border-gray-100" /><span className="text-[10px] text-[#6B7280]">Off / Not Scheduled</span></div>
        </div>
      </div>

      {/* Team Member List with Edit Buttons */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-3">
          <Users size={16} weight="duotone" className="text-[#1E3A8A]" /> Team Members
        </h3>
        <div className="space-y-2">
          {members.map(m => {
            const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.offline;
            const sched = m.schedule;
            const weeklyHours = sched?.weekly_hours;
            const enabledDays = sched?.days ? Object.entries(sched.days).filter(([, v]) => v?.enabled).map(([k]) => k.slice(0, 3).charAt(0).toUpperCase() + k.slice(1, 3)) : [];
            return (
              <div key={m.id} className={`flex items-center gap-3 p-3 rounded-lg border ${cfg.border} ${cfg.bg}`} data-testid={`member-detail-${m.id}`}>
                <div className="relative">
                  <div className="w-9 h-9 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-xs font-bold">
                    {m.name?.[0]?.toUpperCase()}
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${cfg.dot}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-[#0F172A]">{m.name}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#6B7280] font-medium">
                      {m.role?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                  </div>
                  {m.configured ? (
                    <p className="text-[10px] text-[#6B7280]">
                      {m.teams?.join(", ") || "No team"} · {weeklyHours}h/week · {sched?.type === "part_time" ? "Part-time" : "Full-time"} · {enabledDays.join(", ")}
                    </p>
                  ) : (
                    <p className="text-[10px] text-amber-600 font-medium">Working schedule not configured</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded font-semibold`} style={{ color: cfg.color, backgroundColor: `${cfg.color}15` }}>
                    {cfg.label}
                  </span>
                  {canEdit && (
                    <button
                      onClick={() => openEditor(m)}
                      className="p-1.5 rounded hover:bg-white/80 text-[#6B7280] hover:text-[#1E3A8A] transition-colors"
                      title="Edit working schedule"
                      data-testid={`edit-schedule-${m.id}`}
                    >
                      <Pencil size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Editor dialog */}
      {editUserId && (
        <ScheduleEditor
          userId={editUserId}
          userName={editUserName}
          open={!!editUserId}
          onClose={() => setEditUserId(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
